/**
 * COMPONENT: BedSchoolField
 * École du participant (cahier L46) : autocomplétion sur les écoles déjà en
 * base, et si elle n'existe pas, CRÉATION automatique — avec la ville, que le
 * cahier rend obligatoire au même titre que le nom.
 *
 * L'autocomplete existant (`SchoolAutocomplete`, RPC anon `search_schools`) est
 * réutilisé tel quel ; ce composant ajoute la ville et le bouton de création
 * (RPC `bed_create_school`, migration 00177 — nom ET ville exigés côté serveur).
 */

import { CheckCircle2, Loader2, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createBedSchool } from '../../lib/bed/draft';
import { Button, SchoolAutocomplete } from '../ui';
import type { FormFieldWithValues } from '../../types/form';

interface BedSchoolFieldProps {
  token: string;
  schoolField: FormFieldWithValues;
  cityField?: FormFieldWithValues;
  schoolValue: string;
  cityValue: string;
  cityRequired: boolean;
  onChange: (fieldId: string, value: string) => void;
  disabled?: boolean;
}

type CreationState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'created'; name: string; city: string }
  | { kind: 'error'; message: string };

export function BedSchoolField({
  token,
  schoolField,
  cityField,
  schoolValue,
  cityValue,
  cityRequired,
  onChange,
  disabled = false,
}: BedSchoolFieldProps) {
  const [creation, setCreation] = useState<CreationState>({ kind: 'idle' });

  // Changer d'école invalide la confirmation de création précédente.
  useEffect(() => {
    setCreation((prev) =>
      prev.kind === 'created' && prev.name !== schoolValue ? { kind: 'idle' } : prev
    );
  }, [schoolValue]);

  const canCreate =
    !disabled &&
    schoolValue.trim().length >= 2 &&
    cityValue.trim().length >= 2 &&
    Boolean(cityField);

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreation({ kind: 'saving' });
    const result = await createBedSchool(token, schoolValue.trim(), cityValue.trim());

    if (result.status === 'ok') {
      // La RPC normalise / déduplique : on réaligne le champ sur ce qu'elle a
      // réellement enregistré, pour que le trigger de soumission retrouve
      // l'école par son nom exact.
      onChange(schoolField.id, result.name);
      if (cityField) onChange(cityField.id, result.city);
      setCreation({ kind: 'created', name: result.name, city: result.city });
      return;
    }

    setCreation({
      kind: 'error',
      message:
        result.status === 'missing_city'
          ? 'Le nom ET la ville de l’école sont obligatoires.'
          : 'La création de l’école a échoué. Votre saisie sera enregistrée telle quelle.',
    });
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <label htmlFor={schoolField.id} className="block text-sm font-medium text-zinc-900">
          {schoolField.label}
        </label>
        <SchoolAutocomplete
          id={schoolField.id}
          value={schoolValue}
          onChange={(next) => onChange(schoolField.id, next)}
          placeholder={schoolField.placeholder || 'Tapez le nom de votre école…'}
        />
        <p className="text-xs text-zinc-500">
          Votre école n’apparaît pas ? Renseignez sa ville, puis ajoutez-la.
        </p>
      </div>

      {cityField && (
        <div className="space-y-1.5">
          <label htmlFor={cityField.id} className="block text-sm font-medium text-zinc-900">
            {cityField.label}
            {cityRequired && <span className="ml-1 text-red-500">*</span>}
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              id={cityField.id}
              type="text"
              value={cityValue}
              disabled={disabled}
              onChange={(event) => onChange(cityField.id, event.target.value)}
              placeholder={cityField.placeholder || 'Ville'}
              className="h-10 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleCreate}
              disabled={!canCreate || creation.kind === 'saving'}
              className="shrink-0"
            >
              {creation.kind === 'saving' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Ajouter cette école
            </Button>
          </div>
        </div>
      )}

      {creation.kind === 'created' && (
        <p className="flex items-center gap-1.5 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          École enregistrée : {creation.name} — {creation.city}
        </p>
      )}
      {creation.kind === 'error' && <p className="text-sm text-amber-700">{creation.message}</p>}
    </div>
  );
}
